import { keepPreviousData, useQuery } from '@tanstack/react-query';
import getFormFieldValues from '../lib/getFormFieldValues';
import getFormVariantValues from '../lib/getFormVariantValues';
import useCheckoutState from './useCheckoutState';
import api from '../api';
import getTaxAmounts from '../lib/getTaxAmounts';
import getTotalTaxAmounts from '../lib/getTotalTaxAmounts';
import { taxCalculationQueryKey } from '../queryKeys';
import getTicketTypeValues from '../lib/getTicketTypeValues';
import useDebounce from './useDebounce';

export function useCalculateTaxQuery(
  {
    sellerId,
    pageId,
    fields,
    variants,
    tickets,
    couponId,
    customerId,
    dynamicPrice,
    payWhatYouWantPrice,
    selectedPaymentMethod,
  },
  taxEnabled,
) {
  const queryKey = taxCalculationQueryKey({
    sellerId,
    pageId,
    fields,
    variants,
    tickets,
    couponId,
    customerId,
    dynamicPrice,
    payWhatYouWantPrice,
    selectedPaymentMethod,
  });

  return useQuery({
    queryKey,
    queryFn: () =>
      api.calculateTax(
        {
          fields,
          variants,
          tickets,
          couponId,
          customerId,
          dynamicPrice,
          payWhatYouWantPrice,
          selectedPaymentMethod,
        },
        sellerId,
        pageId,
      ),
    enabled: taxEnabled && !!pageId && !!sellerId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    keepPreviousData: keepPreviousData,
  });
}

export default function useTaxCalculation(taxEnabled) {
  const {
    page,
    checkout,
    seller,
    form,
    product,
    coupon,
    isUpsell,
    upsell,
    dynamicPrice,
    selectedPaymentMethod,
    payWhatYouWantPrice,
  } = useCheckoutState();

  const variants = getFormVariantValues(product.variants, form.variants);
  const { fields } = getFormFieldValues(form.fields, page.fields);

  const debouncedFields = useDebounce(fields, 1000);

  const tickets = getTicketTypeValues(checkout.ticketGroups, form.ticketGroups);

  const couponId = coupon?._id;

  const { data, isRefetching } = useCalculateTaxQuery(
    {
      productType: product.type,
      sellerId: seller?._id,
      pageId: checkout?._id,
      fields: debouncedFields,
      variants,
      tickets,
      dynamicPrice,
      couponId,
      customerId: isUpsell ? upsell?.customerId : null,
      payWhatYouWantPrice,
      selectedPaymentMethod,
    },
    taxEnabled,
  );

  const recurringTaxAmounts = getTaxAmounts(data?.recurringTaxAmounts);
  const recurringTaxAmountsBeforeProration = getTaxAmounts(
    data?.recurringTaxAmountsBeforeProration,
  );
  const oneTimeTaxAmounts = getTaxAmounts(data?.oneTimeTaxAmounts);
  const totalTaxAmounts = getTotalTaxAmounts(recurringTaxAmounts, oneTimeTaxAmounts);
  const totalTaxAmountsBeforeProration = getTotalTaxAmounts(
    recurringTaxAmountsBeforeProration,
    oneTimeTaxAmounts,
  );

  return {
    recurringTaxAmount: data?.recurringTaxAmount || 0,
    recurringAmountExcludingTax: data?.recurringAmountExcludingTax || 0,
    recurringTaxAmounts,

    recurringTaxAmountBeforeProration: data?.recurringTaxAmountBeforeProration || 0,
    recurringAmountExcludingTaxBeforeProration:
      data?.recurringAmountExcludingTaxBeforeProration || 0,
    recurringTaxAmountsBeforeProration,

    oneTimeTaxAmount: data?.oneTimeTaxAmount || 0,
    oneTimeTotalAmountExcludingTax: data?.oneTimeTotalAmountExcludingTax || 0,
    oneTimeTaxAmounts,
    oneTimeTotalIncludingTax: data?.oneTimeTotal || 0,

    totalTaxAmounts,
    totalTaxAmountsBeforeProration,
    isCalculatingTax: isRefetching,
  };
}
